import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGeneralSettings } from '../hooks/useGeneralSettings';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { Logo } from './Logo';
import { ProfileModal } from './ProfileModal';
import { LegalDialog } from './legal/LegalDialog';
import { EventNotificationBell } from './events/EventNotificationBell';
import { formatUserDateTime } from '../utils/dateFormat';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Switch } from './ui/switch';
import { InstallAppBanner } from './shared/InstallAppBanner';
import { QuietHoursBadge } from './shared/QuietHoursBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  MapPin,
  Video,
  Map,
  BarChart3,
  Users,
  Truck,
  Calendar,
  MessageSquare,
  MessageCircle,
  Settings,
  Shield,
  HardDrive,
  Terminal,
  Moon,
  Sun,
  LogOut,
  User,
  Menu,
  X,
  Megaphone,
  ChevronDown,
  Bell,
  Check,
  Volume2,
  VolumeX,
  Wrench,
  FileText,
  ShieldCheck,
  IdCard,
  Phone,
} from 'lucide-react';
import { useGroupCall } from '../contexts/GroupCallContext';
import { useIsMobileOrWebview } from '../hooks/useIsMobileOrWebview';

// Map nav items to their feature flag names
const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', mobileLabel: 'Dashboard', path: '/dashboard', featureFlag: 'dashboard_page' },
  { icon: MapPin, label: 'Live Tracking', mobileLabel: 'Tracking', path: '/tracking', featureFlag: 'live_tracking_page' },
  { icon: Video, label: 'Media', mobileLabel: 'Media', path: '/media', featureFlag: 'media_page' },
  { icon: BarChart3, label: 'Analytics', mobileLabel: 'Analytics', path: '/analytics', featureFlag: 'analytics_page' },
  { icon: Map, label: 'Geofencing', mobileLabel: 'Geofencing', path: '/geofencing', featureFlag: 'geofencing_page' },
  { icon: Users, label: 'People', mobileLabel: 'People', path: '/people', featureFlag: 'people_page' },
  { icon: Truck, label: 'Fleet', mobileLabel: 'Fleet', path: '/fleet', featureFlag: 'fleet_page' },
  { icon: Calendar, label: 'Operations', mobileLabel: 'Operations', path: '/operations', featureFlag: 'operations_page' },
  { icon: MessageSquare, label: 'Chat', mobileLabel: 'Chat', path: '/chat', featureFlag: 'chat_page' },
  { icon: IdCard, label: 'My Contact', mobileLabel: 'Contact', path: '/my-contact', featureFlag: 'my_contact_page' },
  { icon: Wrench, label: 'Installation', mobileLabel: 'Install', path: '/installation', featureFlag: 'installation_page' },
  { icon: Settings, label: 'Settings', mobileLabel: 'Settings', path: '/settings', featureFlag: 'settings_page' },
  { icon: Shield, label: 'Admin Panel', mobileLabel: 'Admin', path: '/admin-panel', featureFlag: 'admin_panel_page', adminOnly: true, allowedRoles: ['super_admin', 'admin', 'manager', 'end_user'] },
];

export const Layout = ({ children }) => {
  const {
    user,
    logout,
    api,
    isAdmin,
    soundEnabled,
    chatSoundChoice,
    setChatSoundChoice,
    chatSoundOptions,
    playNotificationSound,
    totalUnreadMessages,
    setChatEnabled,
    onDeviceUpdate,
  } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { features, isPageEnabled, isSubpageEnabled } = useFeatureFlags();
  const { activeConvCalls, groupCall, joinGroupCall } = useGroupCall();
  const isMobileOrWebview = useIsMobileOrWebview();

  // Admin broadcast banners targeted at this user (User Management > Send Banner).
  const [adminBanners, setAdminBanners] = useState([]);
  useEffect(() => {
    if (!api) return undefined;
    let alive = true;
    const load = () => api.get('/banners/active')
      .then((r) => { if (alive) setAdminBanners(r.data || []); })
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [api]);
  const dismissAdminBanner = async (id) => {
    setAdminBanners((prev) => prev.filter((b) => b.id !== id));
    try { await api.post(`/banners/${id}/dismiss`); } catch { /* best-effort */ }
  };

  // Session heartbeat (Option A audit tracking): a tiny "app is open" ping every
  // 3 minutes, only while the tab is visible, so the inactivity sweeper can audit
  // when a session really stopped being used (browser/phone closed, etc.).
  useEffect(() => {
    if (!api || !user?.id) return undefined;
    const beat = () => {
      if (document.visibilityState === 'visible') {
        api.post('/session/heartbeat').catch(() => {});
      }
    };
    beat();
    const t = setInterval(beat, 180000);
    document.addEventListener('visibilitychange', beat);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', beat); };
  }, [api, user?.id]);
  const userMenuItemCls = isMobileOrWebview ? 'min-h-[52px] text-sm px-3 py-3' : '';
  const userMenuIconCls = isMobileOrWebview ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [legalDialog, setLegalDialog] = useState({ open: false, doc: 'terms' });
  const [activeAlertCount, setActiveAlertCount] = useState(0);
  const [myPhotoUrl, setMyPhotoUrl] = useState(null);
  const [myPhotoVersion, setMyPhotoVersion] = useState(0);

  // Header avatar photo: the account's own uploaded photo, own person photo, or
  // the photo of a Driver/Staff record linked to this account.
  useEffect(() => {
    if (!api || !user?.id) { setMyPhotoUrl(null); return undefined; }
    let cancelled = false; let obj = null;
    api.get('/me/person-photo', { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setMyPhotoUrl(obj); } })
      .catch(() => { if (!cancelled) setMyPhotoUrl(null); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, myPhotoVersion]);

  // Active condition/health alert count for the Analytics nav badge.
  // Only for users with the Alerts subpage; polls + refreshes on live WS alerts.
  const alertsBadgeEnabled = isPageEnabled('analytics_page') && isSubpageEnabled('analytics_alerts');
  useEffect(() => {
    if (!alertsBadgeEnabled || !api) { setActiveAlertCount(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/alerts/stats');
        if (!cancelled) setActiveAlertCount(res.data?.active || 0);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60000);
    let unsub;
    if (onDeviceUpdate) {
      unsub = onDeviceUpdate((_deviceId, data) => {
        if (data?.type === 'alert') load();
      });
    }
    // Instant same-tab update when the Alerts page changes state (toggle/ack/resolve).
    const onAlertsChanged = (e) => {
      if (e?.detail && typeof e.detail.active === 'number') setActiveAlertCount(e.detail.active);
      else load();
    };
    window.addEventListener('ensue:alerts-changed', onAlertsChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      if (unsub) unsub();
      window.removeEventListener('ensue:alerts-changed', onAlertsChanged);
    };
  }, [alertsBadgeEnabled, api, onDeviceUpdate]);

  // Sync chat enabled state with feature flags
  useEffect(() => {
    const chatPageEnabled = isPageEnabled('chat_page');
    setChatEnabled(chatPageEnabled);
  }, [isPageEnabled, setChatEnabled]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'SUPER ADMIN', color: 'bg-[#2563eb]' },
      admin: { label: 'ADMINISTRATOR', color: 'bg-[#2563eb]' },
      manager: { label: 'MANAGER', color: 'bg-slate-600' },
      end_user: { label: 'USER', color: 'bg-red-500' },
      sub_account: { label: 'SUB ACCOUNT', color: 'bg-slate-400' },
    };
    return badges[role] || { label: 'USER', color: 'bg-slate-500' };
  };

  const roleBadge = getRoleBadge(user?.role);
  const selectedChatSound = chatSoundOptions?.find(option => option.id === chatSoundChoice) || chatSoundOptions?.[0] || { id: 'default', label: 'Default' };
  const currentNavItem = navItems.find((item) => location.pathname === item.path)
    || (location.pathname.startsWith('/admin/') ? { mobileLabel: 'Admin' } : null);
  const mobilePageTitle = currentNavItem?.mobileLabel || currentNavItem?.label || 'Ensue';

  // Check if current session is impersonated (sessionStorage is tab-isolated)
  const isImpersonated = typeof window !== 'undefined' && sessionStorage.getItem('impersonate_token');

  // Impersonation context for the banner (who → whom · started when)
  const impersonatedByName = typeof window !== 'undefined' ? sessionStorage.getItem('impersonate_by_name') : null;
  const impersonationSessionId = typeof window !== 'undefined' ? sessionStorage.getItem('impersonate_session_id') : null;
  const impersonationStartedAt = typeof window !== 'undefined' ? sessionStorage.getItem('impersonate_started_at') : null;

  // Regional settings of the impersonated account (so the start time is shown in
  // their configured timezone/format). Fetched only while impersonating.
  const { dateFormat, timeFormat, timezone } = useGeneralSettings({ enabled: !!isImpersonated });
  const impFmt = useMemo(
    () => ({ date: dateFormat, time: timeFormat, tz: timezone }),
    [dateFormat, timeFormat, timezone],
  );
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!isImpersonated) return;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [isImpersonated]);

  const relativeSince = (iso) => {
    if (!iso) return null;
    const start = new Date(iso).getTime();
    if (Number.isNaN(start)) return null;
    const diff = Math.max(0, Math.floor((nowTick - start) / 1000));
    if (diff < 60) return 'just now';
    const mins = Math.floor(diff / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hrs}h ${remMins}m ago` : `${hrs}h ago`;
  };

  // Handle ending impersonation — log the end server-side, then clear + logout.
  const handleEndImpersonation = async () => {
    if (impersonationSessionId && api) {
      try {
        await api.post(`/users/impersonation/${impersonationSessionId}/end`);
      } catch (e) {
        // Non-blocking: still end the local session even if the audit call fails.
        console.error('Failed to log impersonation end:', e);
      }
    }
    sessionStorage.removeItem('impersonate_token');
    sessionStorage.removeItem('impersonate_user');
    sessionStorage.removeItem('impersonate_by_name');
    sessionStorage.removeItem('impersonate_session_id');
    sessionStorage.removeItem('impersonate_started_at');
    logout();
    navigate('/login');
  };

  useEffect(() => {
    // Close the mobile nav on route change (incl. browser back/forward). This is
    // an intentional URL→UI sync that react-hooks/set-state-in-effect over-flags.

    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Filter nav items based on feature flags and admin status
  const filteredNavItems = navItems.filter(item => {
    // Check if item has specific allowed roles
    if (item.allowedRoles) {
      if (!item.allowedRoles.includes(user?.role)) return false;
    } else if (item.adminOnly && !isAdmin) {
      // For other admin-only items without allowedRoles, filter for non-admins
      return false;
    }
    // If no feature flag specified, always show
    if (!item.featureFlag) return true;
    // Check if feature is enabled (must be explicitly true)
    return features.pages[item.featureFlag] === true;
  });

  const allNavItems = filteredNavItems;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <InstallAppBanner />
      {/* Impersonation Banner */}
      {isImpersonated && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 z-[1100]" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm min-w-0 truncate" data-testid="impersonation-banner-text">
              You are viewing as <strong data-testid="impersonation-target-name">{user?.full_name || user?.email}</strong>
              {impersonatedByName && (
                <> · by <strong data-testid="impersonation-admin-name">{impersonatedByName}</strong></>
              )}
              {impersonationStartedAt && (
                <span className="hidden sm:inline opacity-90" data-testid="impersonation-started">
                  {' '}· started {formatUserDateTime(impersonationStartedAt, impFmt.date, impFmt.time, impFmt.tz)}
                  {relativeSince(impersonationStartedAt) ? ` (${relativeSince(impersonationStartedAt)})` : ''}
                </span>
              )}
            </span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="bg-white text-amber-600 hover:bg-amber-50 border-white h-7 text-xs flex-shrink-0"
            onClick={handleEndImpersonation}
            data-testid="impersonation-end-btn"
          >
            <LogOut className="w-3 h-3 mr-1" />
            End Session
          </Button>
        </div>
      )}

      {/* Ongoing group call banner — visible on every page until the call ends */}
      {(() => {
        const entry = Object.entries(activeConvCalls || {}).find(([, c]) => groupCall?.call_id !== c.call_id);
        if (!entry) return null;
        const [convId, c] = entry;
        return (
          <div className="bg-green-600 text-white px-4 py-2 flex items-center justify-between gap-3 z-[1100]" data-testid="ongoing-group-call-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse shrink-0" aria-hidden="true" />
              <span className="text-sm min-w-0 truncate" data-testid="ongoing-group-call-banner-text">
                Group {c.media === 'video' ? 'video' : 'voice'} call in progress{c.name ? <> in <strong>{c.name}</strong></> : ''}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="bg-white text-green-700 hover:bg-green-50 border-white h-7 text-xs flex-shrink-0"
              onClick={() => joinGroupCall(c.call_id, { id: convId, name: c.name })}
              data-testid="ongoing-group-call-banner-join"
            >
              <Phone className="w-3 h-3 mr-1" />
              Join
            </Button>
          </div>
        );
      })()}

      {/* Admin broadcast banners (info blue / warning red) — stacked with the system banners */}
      {adminBanners
        .filter((b) => !(b.mode === 'timed' && b.expires_at && new Date(b.expires_at) <= new Date()))
        .slice(0, 3).map((b) => (
        <div
          key={b.id}
          className={`${b.severity === 'warning' ? 'bg-red-600' : 'bg-blue-600'} text-white px-4 py-2 flex items-center justify-between gap-3 z-[1100]`}
          data-testid={`admin-banner-${b.id}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Megaphone className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="text-sm min-w-0 break-words" data-testid="admin-banner-text">{b.message}</span>
            {b.link_url && (
              <button
                type="button"
                onClick={() => {
                  api.post(`/banners/${b.id}/click`).catch(() => {});
                  window.open(b.link_url, '_blank', 'noopener,noreferrer');
                }}
                className="shrink-0 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-full px-3 py-1 transition-colors"
                data-testid={`admin-banner-link-${b.id}`}
              >
                {b.link_label || 'Read more'}
              </button>
            )}
          </div>
          {b.mode === 'once' ? (
            <button
              type="button"
              onClick={() => dismissAdminBanner(b.id)}
              className="shrink-0 rounded p-1 hover:bg-white/20 transition-colors"
              aria-label="Dismiss message"
              data-testid={`admin-banner-dismiss-${b.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            b.expires_at && (
              <span className="text-[11px] opacity-80 shrink-0" data-testid="admin-banner-expiry">
                until {formatUserDateTime(b.expires_at)}
              </span>
            )
          )}
        </div>
      ))}

      {/* Top Header */}
      <header className="sticky top-0 z-[1100] bg-white dark:bg-[#0a1120] border-b border-slate-200 dark:border-slate-800" data-testid="header">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6 gap-2">
          {/* Left side - Mobile Menu + Logo */}
          <div className="flex items-center gap-2 min-w-0 flex-1 lg:flex-none lg:gap-3">
            {/* Mobile menu button - LEFT SIDE */}
            <button
              type="button"
              className="lg:hidden flex min-w-0 flex-1 items-center gap-3 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] focus-visible:ring-offset-2 dark:hover:bg-slate-800 dark:focus-visible:ring-offset-slate-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-overlay"
              aria-label={`${mobileMenuOpen ? 'Close' : 'Open'} navigation menu for ${mobilePageTitle}`}
              data-testid="mobile-menu-btn"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md" data-testid="mobile-menu-icon-hit-area">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </span>
              <span className="min-w-0 flex-1" data-testid="mobile-page-title-wrap">
                <span className="block truncate py-0.5 text-base font-semibold leading-6 text-foreground" data-testid="mobile-page-title">
                  {mobilePageTitle}
                </span>
              </span>
            </button>

            {/* Logo: only show on desktop (hidden on mobile) */}
            <div className="hidden lg:block">
              <Logo size="default" />
            </div>
          </div>

          {/* Right side - Controls */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Messages Icon with Unread Badge - Only show if Chat page is enabled */}
            {isPageEnabled('chat_page') && (
              <button
                onClick={() => navigate('/chat')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                title="Messages"
                data-testid="messages-icon"
              >
                <MessageCircle className={`w-4 h-4 ${totalUnreadMessages > 0 ? 'text-[#2563eb]' : 'text-slate-500 dark:text-slate-400'}`} />
                <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">Chat</span>
                {totalUnreadMessages > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1.5" data-testid="messages-unread-badge">
                    {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                  </span>
                )}
              </button>
            )}

            {/* Quiet Hours indicator */}
            <QuietHoursBadge />

            {/* Event Notification Bell */}
            <EventNotificationBell />

            {/* Chat Sound Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  title={`Chat alert sound: ${selectedChatSound.label}`}
                  data-testid="chat-sound-selector-trigger"
                >
                  {soundEnabled ? (
                    <Volume2 className="w-4 h-4 text-[#2563eb]" />
                  ) : (
                    <VolumeX className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline" data-testid="chat-sound-selected-label">
                    {selectedChatSound.label}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" data-testid="chat-sound-selector-menu">
                <DropdownMenuLabel>Chat alert sound</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {chatSoundOptions?.map(option => (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => {
                      setChatSoundChoice(option.id);
                      if (option.id !== 'silent') playNotificationSound(option.id);
                    }}
                    data-testid={`chat-sound-option-${option.id}`}
                  >
                    {option.id === 'silent' ? (
                      <VolumeX className="w-4 h-4 mr-2 text-slate-400" />
                    ) : (
                      <Volume2 className="w-4 h-4 mr-2 text-[#2563eb]" />
                    )}
                    <span className="flex-1">{option.label}</span>
                    {chatSoundChoice === option.id && <Check className="w-4 h-4 text-[#2563eb]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Dark Mode Toggle */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
              <span className="text-xs text-slate-500 dark:text-slate-400">Dark Mode</span>
              <Switch
                checked={isDark}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
                className="data-[state=checked]:bg-[#2563eb]"
              />
            </div>

            {/* User Menu — mobile sizing mirrors the Live Tracking / People overflow menus */}
            {(() => {
              const myHeaderName = (user?.self_display_name_preference === 'company' && (user?.company || '').trim())
                ? user.company.trim()
                : (user?.full_name || 'Admin User');
              return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`flex items-center gap-2 px-2 hover:bg-slate-100 dark:hover:bg-slate-800 ${isMobileOrWebview ? 'h-11' : ''}`} data-testid="user-menu-btn">
                  <Avatar className="w-9 h-9 bg-[#2563eb]">
                    {myPhotoUrl && <AvatarImage src={myPhotoUrl} alt={myHeaderName} className="object-cover" data-testid="header-avatar-photo" />}
                    <AvatarFallback className="bg-[#2563eb] text-white text-sm font-semibold">
                      {getInitials(myHeaderName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-foreground" data-testid="header-user-name">{myHeaderName}</p>
                    <p className="text-xs text-muted-foreground">{roleBadge.label.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={isMobileOrWebview ? 'w-64 p-1.5' : 'w-56'} data-testid="user-dropdown">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className={userMenuItemCls} onClick={() => setProfileModalOpen(true)}>
                  <User className={userMenuIconCls} />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem className={userMenuItemCls} onClick={() => navigate('/settings')}>
                  <Settings className={userMenuIconCls} />
                  Settings
                </DropdownMenuItem>
                {/* Mobile dark mode toggle */}
                <DropdownMenuItem onClick={toggleTheme} className={`sm:hidden ${userMenuItemCls}`}>
                  {isDark ? <Sun className={userMenuIconCls} /> : <Moon className={userMenuIconCls} />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className={userMenuItemCls} onClick={() => setLegalDialog({ open: true, doc: 'terms' })} data-testid="menu-terms-conditions">
                  <FileText className={userMenuIconCls} />
                  Terms & Conditions
                </DropdownMenuItem>
                <DropdownMenuItem className={userMenuItemCls} onClick={() => setLegalDialog({ open: true, doc: 'privacy' })} data-testid="menu-privacy-policy">
                  <ShieldCheck className={userMenuIconCls} />
                  Privacy Policy
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className={`text-red-600 ${userMenuItemCls}`} data-testid="logout-btn">
                  <LogOut className={userMenuIconCls} />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
              );
            })()}
          </div>
        </div>

        {/* Horizontal Navigation Bar */}
        <nav className="hidden lg:block border-t border-slate-200 dark:border-slate-800" data-testid="main-nav">
          <div className="flex items-center px-4 overflow-x-auto scrollbar-hide">
            {allNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                    ${isActive 
                      ? 'text-[#2563eb] border-[#2563eb]' 
                      : 'text-slate-600 dark:text-slate-400 border-transparent hover:text-[#2563eb] hover:border-slate-300 dark:hover:border-slate-600'
                    }
                  `}
                >
                  {item.label}
                  {item.path === '/analytics' && activeAlertCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1" data-testid="analytics-alerts-nav-badge">
                      {activeAlertCount > 99 ? '99+' : activeAlertCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 top-16 z-[1040] bg-black/10 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close navigation menu"
              data-testid="mobile-nav-backdrop"
            />
            <div
              id="mobile-navigation-overlay"
              className="absolute left-0 right-0 top-full z-[1090] lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0a1120] shadow-2xl"
              data-testid="mobile-nav"
            >
            <nav className="px-4 py-4 space-y-2 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain">
              {allNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex min-h-[52px] items-center gap-3.5 px-4 py-3 rounded-xl text-base font-semibold transition-colors
                      ${isActive 
                        ? 'bg-[#2563eb] text-white' 
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {item.path === '/analytics' && activeAlertCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1.5" data-testid="analytics-alerts-nav-badge-mobile">
                        {activeAlertCount > 99 ? '99+' : activeAlertCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
          </>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto">
        <div className={location.pathname === '/tracking' ? 'p-0 lg:p-6' : 'p-4 lg:p-6'}>
          {children}
        </div>
      </main>

      {/* Profile Modal */}
      <ProfileModal open={profileModalOpen} onOpenChange={setProfileModalOpen} onPhotoChanged={() => setMyPhotoVersion((v) => v + 1)} />

      {/* Legal documents (Terms & Conditions / Privacy Policy) */}
      <LegalDialog
        open={legalDialog.open}
        onOpenChange={(open) => setLegalDialog((prev) => ({ ...prev, open }))}
        doc={legalDialog.doc}
      />
    </div>
  );
};
